import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, X, ShieldCheck, FileSearch } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { HKBadge } from "@/components/site/HKBadge";
import { SectionHeading } from "@/components/site/SectionHeading";
import { CTASection } from "@/components/site/CTASection";
import { PRICING_PLANS, BRAND } from "@/lib/content";
import { Seo } from "@/components/site/Seo";
import { PricingComparisonTable } from "@/components/site/PricingComparisonTable";

// PRICING_PLANS is a module-level constant; partition it once at module scope.
const STANDARD_PLANS = PRICING_PLANS.filter((p) => !p.enterprise);
const ENTERPRISE_PLANS = PRICING_PLANS.filter((p) => p.enterprise);

export default function PricingPage() {
  const { t } = useTranslation();
  const faqs = t("content.pricingFaqs", { returnObjects: true }) || [];
  const willNotItems = t("common.whatWeWillNotDoItems", { returnObjects: true }) || [];
  const breadcrumbs = [
    { name: t("seo.breadcrumbHome"), url: "/" },
    { name: t("seo.pricing.breadcrumbLabel"), url: "/pricing" },
  ];
  return (
    <div data-testid="pricing-page">
      <Seo
        title={t("seo.pricing.metaTitle")}
        description={t("seo.pricing.metaDescription")}
        keywords={t("seo.pricing.keywords")}
        path="/pricing"
        breadcrumbs={breadcrumbs}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "DC Dashboard",
          "description": "One-stop media-buying console with agency ad accounts, prepaid wallet, multi-channel campaign control, and audit-ready reporting.",
          "brand": { "@type": "Brand", "name": "Danny Clans Media" },
          "offers": PRICING_PLANS.map((p) => ({
            "@type": "Offer",
            "name": p.tk.split(".").pop(),
            "price": p.price.replace(/[^0-9]/g, ""),
            "priceCurrency": "USD",
            "url": "https://dannyclans.solutions/pricing",
          })),
        }}
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dc-dot-grid opacity-50" aria-hidden />
        <div className="relative mx-auto w-full max-w-6xl px-4 pb-10 pt-14 sm:px-6 sm:pb-14 sm:pt-20 lg:px-8">
          <HKBadge />
          <h1 className="mt-5 max-w-4xl text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-[58px] lg:leading-[1.05]">
            {t("pages.pricingTitle")}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-600 sm:text-lg">{t("pages.pricingSubtitle")}</p>
          <p className="mt-3 text-xs font-bold uppercase tracking-[0.22em] text-blue-700">{BRAND.marketingLine}</p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800" data-testid="pricing-indicative-badge">
            <FileSearch className="h-4 w-4" />
            {t("common.standardEnterprise")}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
        <div className="grid gap-5 lg:grid-cols-4">
          {STANDARD_PLANS.map((b, idx) => {
            const includes = t(`content.${b.tk}.includes`, { returnObjects: true }) || [];
            const limits = t(`content.${b.tk}.limits`, { returnObjects: true }) || [];
            return (
              <motion.div
                key={b.key}
                initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.35, delay: idx * 0.04 }}
                data-testid={`pricing-band-${b.key}`}
                className={`relative flex flex-col rounded-2xl border p-6 sm:p-7 ${b.highlighted ? "border-blue-600 bg-blue-50/30 shadow-[0_8px_24px_rgba(37,99,235,0.08)]" : "border-slate-200 bg-white"}`}
              >
                {b.highlighted && (
                  <span className="absolute -top-3 left-6 inline-flex items-center rounded-full bg-blue-600 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">{t("common.mostPopular")}</span>
                )}
                <h3 className="text-base font-extrabold uppercase tracking-wider text-slate-900">{t(`content.${b.tk}.name`)}</h3>
                <p className="mt-1 text-xs text-slate-500">{t(`content.${b.tk}.tagline`)}</p>
                <div className="mt-5 flex items-end gap-1">
                  <span className="text-3xl font-extrabold tracking-tight text-slate-900">{b.price}</span>
                  <span className="pb-1 text-xs font-medium text-slate-500">{b.cadence}</span>
                </div>
                <p className="mt-1 text-xs text-slate-500">{t(`content.${b.tk}.target`)}</p>
                <ul className="mt-5 space-y-2 text-sm text-slate-700">
                  {includes.map((it) => (<li key={it} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> <span>{it}</span></li>))}
                </ul>
                {limits.length > 0 && (
                  <div className="mt-5 border-t border-slate-200 pt-4">
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t("common.limitations")}</div>
                    <ul className="mt-2 space-y-1.5 text-xs text-slate-500">
                      {limits.map((it) => (<li key={it} className="flex items-start gap-2"><X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" /> <span>{it}</span></li>))}
                    </ul>
                  </div>
                )}
                <Link to="/contact" className="mt-6 inline-block" data-testid={`pricing-band-cta-${b.key}`}>
                  <Button className={`h-11 w-full rounded-lg ${b.highlighted ? "bg-blue-600 text-white hover:bg-blue-700" : "border border-slate-300 bg-white text-slate-900 hover:bg-slate-50"}`}>
                    {t(`content.${b.tk}.cta`)} <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Button>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      <PricingComparisonTable />

      {ENTERPRISE_PLANS.map((b) => {
        const includes = t(`content.${b.tk}.includes`, { returnObjects: true }) || [];
        return (
          <section key={b.key} className="mx-auto w-full max-w-7xl px-4 pb-10 sm:px-6 lg:px-8" data-testid={`pricing-band-${b.key}`}>
            <div className="relative overflow-hidden rounded-3xl border border-slate-900 bg-slate-900 p-8 text-white sm:p-12">
              <div className="absolute inset-0 opacity-20 dc-dot-grid" aria-hidden />
              <div className="relative grid gap-10 lg:grid-cols-5 lg:items-start">
                <div className="lg:col-span-2">
                  <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-emerald-300">
                    {t("common.enterpriseWhitelabel")}
                  </div>
                  <h3 className="mt-4 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">{t(`content.${b.tk}.name`)}</h3>
                  <p className="mt-3 text-sm uppercase tracking-[0.22em] text-blue-300">{t(`content.${b.tk}.tagline`)}</p>
                  <div className="mt-6 flex items-end gap-1">
                    <span className="text-4xl font-extrabold text-white">{b.price}</span>
                    <span className="pb-2 text-sm text-slate-300">{b.cadence}</span>
                  </div>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-300">{t(`content.${b.tk}.target`)}</p>
                  <Link to="/contact" className="mt-6 inline-block" data-testid={`pricing-band-cta-${b.key}`}>
                    <Button className="h-11 rounded-lg bg-white px-5 text-slate-900 hover:bg-slate-100">
                      {t(`content.${b.tk}.cta`)} <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
                <div className="lg:col-span-3">
                  <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-blue-300">{t("common.included")}</h4>
                  <ul className="mt-3 grid gap-2 text-sm text-slate-200 sm:grid-cols-2">
                    {includes.map((it) => (<li key={it} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> <span>{it}</span></li>))}
                  </ul>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <section className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-7" data-testid="pricing-disclaimer">
          <div className="flex items-center gap-2 text-emerald-700"><ShieldCheck className="h-5 w-5" /><span className="text-sm font-semibold">{t("common.whatWeWillNotDoTitle")}</span></div>
          <ul className="mt-3 space-y-2 text-sm text-slate-700">
            {willNotItems.map((it) => (
              <li key={it} className="flex items-start gap-2"><span className="mt-1 inline-flex h-1.5 w-1.5 rounded-full bg-red-500" /> {it}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-slate-50" data-testid="pricing-faq-section">
        <div className="mx-auto w-full max-w-3xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <SectionHeading eyebrow={t("sections.faqPricing.eyebrow")} title={t("sections.faqPricing.title")} align="center" testIdPrefix="pricing-faq" />
          <div className="mt-8 space-y-3">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl border border-slate-200 bg-white p-5" data-testid={`pricing-faq-${f.q.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}>
                <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold text-slate-900">
                  {f.q}
                  <span className="ml-4 text-slate-400 transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CTASection title={t("ctaSection.pricingTitle")} primaryLabel={t("cta.primary")} />
    </div>
  );
}
